<template>
  <div class="curriculum">
    <div class="container">
      <div class="heading row">
        <h2 class="h2 col col-10 offset-1">
          <slot v-if="$slots.title" name="title" />
          <template v-else>
            {{ heading }}
          </template>
        </h2>
        <span v-if="subtitle" class="col col-10 offset-1">
          {{ subtitle }}
        </span>
        <span v-else class="col col-10 offset-1">
          {{ subHeading }}
        </span>
      </div>
      <div class="modules">
        <div class="tabs">
          <div :class="{ active: currentModule === 0 }" @click="changeModule(0)">
            Module 1
          </div>
          <div :class="{ active: currentModule === 1 }" @click="changeModule(1)">
            Module 2
          </div>
          <div :class="{ active: currentModule === 2 }" @click="changeModule(2)">
            Module 3
          </div>
          <div :class="{ active: currentModule === 3 }" @click="changeModule(3)">
            Module 4
          </div>
        </div>
        <div
          v-for="(module, i) in modules" :key="i" class="module"
          :class="{ show: i === currentModule, expand: moduleSeeMore && $store.state.layout === 'mobile' }"
        >
          <div class="module-mobile" @click="changeModule(i)">
            <span v-if="campaign === 'most-wanted' || campaign === 'data-science-hero'">
              Module {{ i + 1 }}
            </span>
            <span v-else>
              Module {{ i + 1 }}
              <IconArrowDown />
            </span>
          </div>
          <client-only>
            <div v-show="$store.state.layout === 'mobile'" class="module-see-more">
              <div @click.prevent="expandModule">
                <span v-if="moduleSeeMore">See Less <IconArrowDown /></span>
                <span v-else class="expand">See More <IconArrowDown /></span>
              </div>
            </div>
          </client-only>
          <div class="module-body">
            <h3 class="h3">
              {{ module.title }}
            </h3>
            <p class="body-2">
              {{ module.subtitle }}
            </p>
            <div class="module-info">
              <div v-for="(group, idx) in dataStructure(i)" :key="idx" class="course col-6">
                <div v-for="(course, ic) in group" :key="ic">
                  <div class="course-info">
                    <span class="number">{{ ('0' + getCourseId(course.name)).slice(-2) }}</span>
                    <span class="name" :title="course.name">{{ course.name }}</span>
                    <span class="lessons">{{ course.lessons }} lessons</span>
                  </div>
                  <hr>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer-icons col-10 offset-1">
        <div>
          <ClockIcon />
          <div class="description">
            <template v-if="statsHardcoded.courses && !useFreeStats">
              <span>{{ statsHardcoded.courses }}</span>
              Courses
            </template>
            <template v-else>
              <span>{{ videoDuration }}</span>
              Hours of Video
            </template>
          </div>
        </div>
        <div>
          <IconMonitorPlay class="icon-exercises" />
          <div class="description">
            <span>{{ exercises }}</span>
            Exercises
          </div>
        </div>
        <div>
          <IconPeople class="icon-students" />
          <div class="description">
            <span>{{ $c.text('students.count') }}+</span>
            Students
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { merge } from 'lodash-es';
import ClockIcon from '@itbpbg/lms-components/src/assets/svg/courses/clock-colored.svg?vue-component';
import IconPeople from '../../assets/svg/icon-blog-meet-the-team.svg?vue-components';
import IconMonitorPlay from '../../assets/svg/icon-monitor-play.svg?vue-components';
import IconArrowDown from '../../assets/svg/icon-expand.svg?vue-component';

export default {
  components: {
    ClockIcon,
    IconPeople,
    IconMonitorPlay,
    IconArrowDown,
  },
  props: {
    title: { type: String, default: '', required: false },
    subtitle: { type: String, default: '', required: false },
    button: { type: String, default: '', required: false },
    modulesExtend: { type: Object, default: () => ({}), required: false },
    campaign: { type: String, default: '', required: false },
    useFreeStats: Boolean,
    floorStats: Boolean,
    statsHardcoded: { type: Object, default: () => ({}), required: false },
    version: { type: String, default: '0', required: false },
  },
  data() {
    return {
      currentModule: 0,
      modules: [
        {
          title: 'Data Science Fundamentals ',
          subtitle: 'This module is optimized to help you develop a strong foundation for a data science career. It dives deep into the core principles of probability, statistics, and mathematics necessary for building machine and deep learning models further in the program. You will start working with data and learn how to visually present the results of your analyses. ',
        },
        {
          title: 'Programming for Data Science ',
          subtitle: 'Here you will focus on developing a versatile programming skillset. You will acquire a thorough functional understanding of relational databases, using essential SQL queries to preprocess data, as well as coding and leveraging popular libraries in Python, like NumPy and matplotlib. You will also learn the best ways to manipulate and visualize data in R. ',
        },
        {
          title: 'Machine & Deep Learning ',
          subtitle: 'Building on the foundations developed in the first 2 modules, Module 3 will teach you how to apply advanced statistical methods to execute predictive analytics. You will learn how to use sklearn to build complete linear and logistic regression models from scratch, and how to cluster unlabeled datasets with k-means. You will then progress to building deep learning models with the Keras library in TensorFlow 2.0, optimizing your neural network with backpropagation and fine-tuning your model.  ',
        },
        {
          title: 'Advanced Specialization ',
          subtitle: 'By now, you will have developed a solid understanding of Python programming and statistical modeling. The concluding module gives you the opportunity to mold your data science expertise according to a field of your choosing. From developing next-gen fintech products to helping retail giants boost profitability through customer analytics, you’ll be able to make a valuable contribution to a diverse industry spectrum. ',
        },
      ],
      stats: {
        rounded: {},
      },
      moduleSeeMore: false,
    };
  },
  async fetch() {
    const [stats, modulesData] = await Promise.all([
      this.$axios.$get('/stats/courses?reduced=1').catch(() => ({})),
      this.$axios.$get('/modules').catch(() => ([])),
    ]);

    // eslint-disable-next-line no-return-assign
    modulesData.map((m, id) => {
      // extend module data
      if (this.modulesExtend[id]) {
        this.modules[id] = merge(this.modules[id], this.modulesExtend[id]);
      }
      if (this.modules[id]) {
        this.$set(this.modules[id], 'courses', m.courses);
      }
      return true;
    });
    if (this.floorStats) {
      stats.rounded.videoDuration -= stats.rounded.videoDuration % 10;
      stats.rounded.exercises -= stats.rounded.exercises % 10;
    }
    this.stats = stats;
  },
  computed: {
    videoDuration() {
      if (this.useFreeStats) {
        return `${this.stats.rounded.freeVideoDuration}+`;
      }

      if (this.statsHardcoded) {
        return this.statsHardcoded.videoDuration;
      }

      return this.stats.rounded.videoDuration;
    },
    exercises() {
      if (this.useFreeStats) {
        return `${this.stats.rounded.freeExercises}+`;
      }

      if (this.statsHardcoded) {
        return this.statsHardcoded.exercises;
      }

      return this.stats.rounded.exercises;
    },
    heading() {
      if (this.title) {
        return this.title;
      }
      if (this.version === '0') {
        return 'Travel Down an Expert-led Learning Path';
      }
      return 'Guidance from Gurus down a Logical Learning Path';
    },
    subHeading() {
      if (this.version === '0') {
        return 'Build your data science skills from the ground up. From the very fundamentals to the most advanced topics, our all-in-one training is perfect for individuals who want to start, swap, or strengthen their data science career with the most in-demand skills and technologies in the industry.';
      }
      return 'Wherever you’re starting from, and wherever you’d like to go, our Data Science experts will guide you to your goals. Whether it’s familiarizing yourself the fundamentals or mastering advanced topics, our thorough curricula is perfect for individuals who want to start, swap, or strengthen their data science career.';
    },
  },
  methods: {
    dataStructure(currentModule = -1) {
      const cols = this.$store.state.layout === 'desktop' || this.$store.state.tablet ? 2 : 1;
      const data = this.modules[currentModule].courses || {};

      const chunkSize = Math.ceil(data.length / cols);
      const dataArray = [];
      for (let i = 0; i < data.length; i += 1) {
        const chunkIndex = Math.floor(i / chunkSize);

        if (!dataArray[chunkIndex]) {
          dataArray[chunkIndex] = [];
        }

        dataArray[chunkIndex].push(data[i]);
      }
      for (let i = 0; i < dataArray.length; i += 1) {
        dataArray[i] = [...dataArray[i]];
      }

      return dataArray;
    },
    getCourseId(name) {
      return this.modules[this.currentModule].courses.findIndex((course) => course.name === name) + 1;
    },
    changeModule(module) {
      this.currentModule = module;
      this.moduleSeeMore = false;
      if (this.$store.state.layout === 'mobile') {
        setTimeout(() => {
          const headerHeight = 68;
          const buffer = 25;
          const scrollToEl = document.querySelector('.module.show');
          const topOfElement = window.pageYOffset + scrollToEl.getBoundingClientRect().top - headerHeight - buffer;
          window.scroll({ top: topOfElement, behavior: 'smooth' });
        }, 500);
      }
    },
    expandModule() {
      this.moduleSeeMore = !this.moduleSeeMore;
      const headerHeight = 68;
      const buffer = 25;
      let scrollToEl;
      if (!this.moduleSeeMore) {
        scrollToEl = document.querySelector('.module.show');
      } else {
        scrollToEl = document.querySelector('.module.show .module-info');
      }
      const topOfElement = window.pageYOffset + scrollToEl.getBoundingClientRect().top - headerHeight - buffer;
      window.scroll({ top: topOfElement, behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss" scoped>
.curriculum {
  .heading {
    text-align: center;
    margin-bottom: 55px;

    span {
      color: var(--main-dark-shade-4);
      font-size: 18px;
    }
  }

  .modules {
    .tabs {
      display: flex;

      > div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 57px;
        background-color: var(--main-dark-shade-2);
        border-radius: 7px 7px 0 0;
        color: #BDBDBD;
        cursor: pointer;

        &:not(:first-of-type) {
          margin-left: var(--spacing-6);
        }

        &.active {
          background-color: var(--main-dark-shade-1);
          color: var(--main-light);
        }
      }
    }

    .module {
      padding: var(--spacing-60) var(--spacing-48);
      background-color: var(--main-dark-shade-1);
      border-radius: 0 0 7px 7px;
      display: none;

      &.show {
        display: block;
      }

      .module-mobile {
        display: none;
      }

      .h3 {
        max-width: 40%;
      }

      .body-2 {
        color: var(--main-dark-shade-4);
        margin-bottom: var(--spacing-48);
        max-width: 50%;
      }

      .module-info {
        display: flex;
        flex-wrap: wrap;

        .course {
          width: 50%;

          &:first-of-type {
            padding-right: var(--spacing-55);
          }

          &:nth-of-type(2) {
            padding-left: var(--spacing-55);
          }

          > div {
            margin-bottom: 18px;

            hr {
              border: none;
              margin: 0;
            }

            hr:after {
              content: '......................................................................................';
              letter-spacing: 13px;
              font-size: 16px;
              color: rgba(255, 255, 255, 0.24);
            }

            &:last-of-type {
              hr {
                display: none;
              }
            }

            .course-info {
              display: flex;
              padding-bottom: 18px;

              .number {
                color: var(--accent);
                margin-right: 11px;
              }

              .name {
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 60%;
              }

              .lessons {
                color: var(--main-dark-shade-4);
                margin-left: auto;
              }
            }
          }
        }
      }
    }
  }

  .footer-icons {
    display: flex;
    justify-content: space-between;
    margin-top: var(--spacing-100);

    > div {
      display: flex;

      svg {
        height: 60px;
        width: 60px;
        margin-right: var(--spacing-20);

        &.icon-exercises path[stroke]:first-of-type {
          stroke: var(--main-light);
        }

        &.icon-students path[stroke] {
          &:first-of-type,
          &:nth-of-type(3) {
            stroke: var(--main-light);
          }
        }
      }

      .description {
        display: flex;
        flex-direction: column;

        span {
          font-size: 40px;
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .modules {
      .tabs {
        display: none;
      }

      .module {
        margin-top: var(--spacing-12);
        margin-bottom: var(--spacing-12);
        padding: 0;
        background: none;
        display: block;
        transition: all var(--duration) ease;
        position: relative;

        &:not(.show) {
          height: 68px;
          overflow: hidden;
        }

        &.show {
          max-height: 700px;
          padding: 40px 20px;
          overflow: hidden;
          background-color: var(--main-dark-shade-1);
          border-radius: 7px;

          .module-mobile {
            background: none;
            cursor: initial;
            height: auto;
            margin-bottom: var(--spacing-20);

            span {
              color: var(--main-light);

              svg {
                display: none;
              }
            }
          }

          .module-see-more {
            display: flex;
          }
        }

        &.expand {
          max-height: 2000px;

          .module-see-more:after {
            background: none;
          }
        }

        .module-mobile {
          height: 68px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 21px;
          background: var(--main-dark-shade-2);
          border-radius: 7px;
          color: #BDBDBD;
          cursor: pointer;

          span {
            display: flex;
            justify-content: center;
            align-items: center;

            svg {
              margin-left: 10px;

              path[fill] {
                fill: #BDBDBD;
              }
            }
          }
        }

        .module-see-more {
          min-height: 20px;
          color: var(--accent);
          margin-top: 10px;
          position: absolute;
          bottom: 20px;
          left: 0;
          width: 100%;
          display: none;

          > div {
            z-index: 1;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
          }

          span {
            font-weight: 600;
            z-index: 1;

            &:not(.expand) {
              svg {
                transform: rotate(180deg);
              }
            }

            svg {
              margin-left: 10px;

              path[fill] {
                fill: var(--accent);
              }
            }
          }

          &:after {
            content: '';
            position: absolute;
            top: -80px;
            height: 120px;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(180deg, rgba(20, 31, 49, 0) -13.64%, #141F31 66.26%);
          }
        }

        .module-body {
          .h3 {
            max-width: 100%;
            font-size: 20px;
          }

          .body-2 {
            max-width: 100%;
          }

          .module-info {
            .course {
              padding-right: 0 !important;
              padding-left: 0 !important;

              &:last-of-type {
                margin-top: var(--spacing-18);
                padding-bottom: 60px;

                > div {
                  &:last-of-type hr {
                    display: none;
                  }
                }
              }

              > div {
                .name {
                  max-width: 100%;
                }

                .lessons {
                  display: none;
                }

                &:last-of-type hr {
                  display: block;
                }
              }
            }
          }
        }
      }
    }

    .footer-icons {
      flex-direction: column;
      justify-content: center;
      align-items: center;

      > div:not(:first-of-type) {
        margin-top: 40px;
      }
    }
  }
}
</style>
